import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as PUBLIC_API from "../../../../app/public_api";
import * as PROTECTED_API from "../../../../app/protected_api";
import {
  PayloadWithCallback,
  ResultItem,
  RootState,
  statusType,
} from "../../../../utils/types";
import { COUNT_PER_PAGE, MESSAGE_DURATION } from "../../../../utils-creation/constants";
import { message } from "antd";
import {
  CreationsGETResponse,
  CreationYearsStatsGETResponse,
  FuzzySearchResearcherID,
  Creation
} from "../../../../utils-creation/types";
import { AxiosError } from "axios";

export interface CreationPanelState {
  creationsList: ResultItem[];
  creationToEdit: Creation | null;
  status: statusType;
  total: number;
  currentPage: number;
  hasNext: boolean;
  yearFilter: {
    total: number;
    year: number;
    years: { year: number; count: number }[];
    status: statusType;
  };
  fuzzySearchResearchersID: {
    results: FuzzySearchResearcherID[];
    status: statusType;
  };
}

const initialState: CreationPanelState = {
  creationsList: [],
  creationToEdit: null,
  status: "idle",
  total: -1,
  currentPage: 0,
  hasNext: false,
  yearFilter: {
    total: -1,
    year: -1,
    years: [],
    status: "idle",
  },
  fuzzySearchResearchersID: {
    results: [],
    status: "idle",
  },
};

export const initList = createAsyncThunk(
  "creationPanel/initList",
  async (
    data: {
      randomID: string;
      year: number;
    },
    thunkAPI
  ) => {
    try {
      const { year, randomID } = data;
      const { currentPage } = (thunkAPI.getState() as RootState).creationPanel;
      return await PUBLIC_API.getCreationsList({
        randomID,
        year,
        page: currentPage + 1,
      });
    } catch (err) {
      console.error((err as AxiosError)?.response?.data);
      return err;
    }
  }
);

export const loadMore = createAsyncThunk(
  "creationPanel/loadMore",
  async (
    data: {
      randomID: string;
      year: number;
    },
    thunkAPI
  ) => {
    try {
      const { year, randomID } = data;
      const { currentPage } = (thunkAPI.getState() as RootState).creationPanel;
      return await PUBLIC_API.getCreationsList({
        randomID,
        year,
        page: currentPage + 1,
      });
    } catch (err) {
      console.error((err as AxiosError)?.response?.data);
      return err;
    }
  }
);

export const getCreationYearsStats = createAsyncThunk(
  "creationPanel/getCreationYearsStats",
  async (randomID: string) => {
    try {
      return await PUBLIC_API.getCreationYearsStats({ randomID });
    } catch (err) {
      console.error((err as AxiosError)?.response?.data);
      return err;
    }
  }
);

export const getFuzzySearchResearchersID = createAsyncThunk(
  "creationPanel/getFuzzySearchResearchersID",
  async (keyword: string) => {
    try {
      return await PUBLIC_API.getFuzzySearchResearchersID(keyword);
    } catch (err) {
      console.error((err as AxiosError)?.response?.data);
      return err;
    }
  }
);

export const postCreation = createAsyncThunk(
  "creationPanel/postCreation",
  async (payloadWithCallback: PayloadWithCallback) => {
    try {
      const data = await PROTECTED_API.postCreation(
        payloadWithCallback?.payload
      );

      message.success("Add Success.", MESSAGE_DURATION);
      payloadWithCallback?.callback?.();
      return data;
    } catch (err) {
      console.error((err as any)?.response?.data);
      message.error("Add Failed.", MESSAGE_DURATION);
      return err;
    }
  }
);

export const getCreation = createAsyncThunk(
  "creationPanel/getCreation",
  async (id: number) => {
    try {
      const data = await PROTECTED_API.getCreation(id);

      return data;
    } catch (err) {
      console.error((err as any)?.response?.data);
      return err;
    }
  }
);

export const putCreation = createAsyncThunk(
  "creationPanel/putCreation",
  async (payloadWithCallback: PayloadWithCallback) => {
    try {
      const data = await PROTECTED_API.putCreation(
        payloadWithCallback?.payload
      );

      message.success("Update Success.", MESSAGE_DURATION);
      payloadWithCallback?.callback?.();
      return data;
    } catch (err) {
      console.error((err as any)?.response?.data);
      message.error("Update Failed.", MESSAGE_DURATION);
      return err;
    }
  }
);

export const deleteCreation = createAsyncThunk(
  "creationPanel/deleteCreation",
  async (payloadWithCallback: PayloadWithCallback) => {
    try {
      const data = await PROTECTED_API.deleteCreation(
        payloadWithCallback?.payload
      );

      message.success("Delete Success.", MESSAGE_DURATION);
      payloadWithCallback?.callback?.();
      return data;
    } catch (err) {
      console.error((err as any)?.response?.data);
      message.error("Delete Failed.", MESSAGE_DURATION);
      return err;
    }
  }
);

const creationPanelSlice = createSlice({
  name: "creationPanel",
  initialState,
  reducers: {
    resetState: () => initialState,
    setYear: (state, action: PayloadAction<number>) => {
      state.yearFilter.year = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // initList
      .addCase(initList.pending, (state) => {
        state.status = "loading";
        state.total = initialState.total;
        state.hasNext = initialState.hasNext;
        state.currentPage = initialState.currentPage;
        state.creationsList = initialState.creationsList;

        // set the list to be COUNT_PER_PAGE skeleton items
        state.creationsList = [...new Array(COUNT_PER_PAGE)].map(() => ({
          loading: true,
          item: null,
        }));
      })
      .addCase(
        initList.fulfilled,
        (state, action: PayloadAction<CreationsGETResponse | unknown>) => {
          const { links, total_result, results } =
            action.payload as CreationsGETResponse;

          state.status = "succeeded";
          state.total = total_result;
          state.hasNext = !!links?.next;
          state.currentPage += 1;

          // set the list to what we received
          state.creationsList = results?.map((x) => ({
            loading: false,
            item: x,
          }));
        }
      )
      .addCase(initList.rejected, (state) => {
        state.status = "failed";
      })
      // loadMore
      .addCase(loadMore.pending, (state) => {
        state.status = "loading";

        // add COUNT_PER_PAGE skeleton items to the list
        state.creationsList = state.creationsList.concat(
          [...new Array(COUNT_PER_PAGE)].map(() => ({
            loading: true,
            item: null,
          }))
        );
      })
      .addCase(
        loadMore.fulfilled,
        (state, action: PayloadAction<CreationsGETResponse | unknown>) => {
          const { links, total_result, results } =
            action.payload as CreationsGETResponse;

          state.status = "succeeded";
          state.total = total_result;
          state.hasNext = !!links.next;
          state.currentPage += 1;

          // replace the last cntPerPage skeleton item list
          // to the list we received
          state.creationsList = state.creationsList
            .slice(0, -COUNT_PER_PAGE)
            .concat(results?.map((x) => ({ loading: false, item: x })));
        }
      )
      .addCase(loadMore.rejected, (state) => {
        state.status = "failed";
      })
      // getCreationYearsStats
      .addCase(getCreationYearsStats.pending, (state) => {
        state.yearFilter.status = "loading";
      })
      .addCase(
        getCreationYearsStats.fulfilled,
        (
          state,
          action: PayloadAction<CreationYearsStatsGETResponse | unknown>
        ) => {
          state.yearFilter.status = "succeeded";

          const { total, years } =
            action.payload as CreationYearsStatsGETResponse;

          state.yearFilter.years = years;
          state.yearFilter.total = total;
        }
      )
      .addCase(getCreationYearsStats.rejected, (state) => {
        state.yearFilter.status = "failed";
      })
      // getFuzzySearchResearchersID
      .addCase(getFuzzySearchResearchersID.pending, (state) => {
        state.fuzzySearchResearchersID.status = "loading";
      })
      .addCase(getFuzzySearchResearchersID.fulfilled, (state, action) => {
        state.yearFilter.status = "succeeded";

        state.fuzzySearchResearchersID.results =
          action.payload as FuzzySearchResearcherID[];
      })
      .addCase(getFuzzySearchResearchersID.rejected, (state) => {
        state.yearFilter.status = "failed";
        state.fuzzySearchResearchersID.results = [];
      })
      // postCreation
      .addCase(postCreation.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postCreation.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(postCreation.rejected, (state) => {
        state.status = "failed";
      })
      // getCreation
      .addCase(getCreation.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        getCreation.fulfilled,
        (state, action: PayloadAction<Creation>) => {
          state.status = "succeeded";
          state.creationToEdit = action.payload;
        }
      )
      .addCase(getCreation.rejected, (state) => {
        state.status = "failed";
        state.creationToEdit = null;
      })
      // putCreation
      .addCase(putCreation.pending, (state) => {
        state.status = "loading";
      })
      .addCase(putCreation.fulfilled, (state) => {
        state.status = "succeeded";
        state.creationToEdit = null;
      })
      .addCase(putCreation.rejected, (state) => {
        state.status = "failed";
        state.creationToEdit = null;
      })
      // deleteCreation
      .addCase(deleteCreation.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteCreation.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(deleteCreation.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const { resetState, setYear } = creationPanelSlice.actions;

export default creationPanelSlice.reducer;
